/**
 * Turns a failed `forge-runtimes/ensure` response into something a person can
 * act on.
 *
 * ## Why this exists
 *
 * CMMD already answers the question. Its 409 branch returns `{ error, code,
 * retryable? }`, and the route says why in its own comment: `code` is the
 * `forge_runtimes.errorCode` vocabulary, present "so both surfaces are
 * diagnosable from the response alone".
 *
 * Forge was rendering `error` alone. For the rotation failures that fall to
 * CMMD's generic fallback message, `error` is the string "Forge runtime
 * reprovisioning failed closed." — which names no cause, so the only way to
 * learn why a runtime refused to provision was to read CMMD's source and logs.
 * The discriminator was in the response the whole time.
 *
 * ## Why a terminal 409 says so out loud
 *
 * The same route emits `retryable: true` ONLY when a retry can help, because
 * "an unmarked 409 stays terminal". The restart affordance does not know that
 * and will happily re-fire a call the protocol has already refused, so a user
 * can retry a permanently-refused runtime indefinitely and learn nothing.
 * Saying it is terminal is the difference between a dead end and a dead end
 * with a sign on it.
 *
 * Scoped to 409 deliberately: `retryable` is defined on that branch. Reading a
 * missing field as "terminal" on a 500 or a gateway error would invent a
 * guarantee the server never made — those genuinely are worth retrying.
 */

export interface EnsureFailureBody {
  readonly code?: unknown;
  readonly error?: unknown;
  readonly retryable?: unknown;
}

const CONFLICT = 409;

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

export function describeEnsureFailure(status: number, body: EnsureFailureBody): string {
  const parts: string[] = [readNonEmptyString(body.error) ?? `CMMD returned ${status}.`];

  const code = readNonEmptyString(body.code);
  if (code) parts.push(`(${code})`);

  if (status === CONFLICT && body.retryable !== true) {
    parts.push("This is terminal — restarting will not clear it.");
  }

  return parts.join(" ");
}
